import { Injectable, OnDestroy } from '@angular/core';
import { map, catchError, finalize } from 'rxjs/operators';
import { Observable, BehaviorSubject, of, Subscription } from 'rxjs';

import { RoleHTTPService } from './http';

import { AppResult, PaginatedList } from '../../models';
import { CreateRoleModel, PremissionModel, RoleModel, RoleSearchModel, UpdateRoleModel, AssignRoleModel } from '../../models/auths';

@Injectable({
   providedIn: 'root',
})
export class RoleService implements OnDestroy {
   // private readonly fields
   private readonly unsubscribe: Subscription[] = [];

   // public fields
   isLoading$: Observable<boolean>;
   isLoadingSubject: BehaviorSubject<boolean>;

   get defaultRole(): RoleModel {
      return {
         arabicDescription: '',
         arabicName: '',
         description: '',
         name: '',
         numberOfUsers: 0,
         id: '',
         permissions: []
      }
   }

   constructor(private readonly RoleHttpService: RoleHTTPService) {
      this.isLoadingSubject = new BehaviorSubject<boolean>(false);
      this.isLoading$ = this.isLoadingSubject.asObservable();
   }

   // public methods
   Search(model: RoleSearchModel): Observable<PaginatedList<RoleModel>> {
      return this.RoleHttpService.Search(model).pipe(
         map((roles: PaginatedList<RoleModel>) => {
            if (roles) {
               return roles;
            }

            return this.getDefaultRole(model.page);
         }),
         catchError((err) => {
            console.error('Role Search error:', err);
            return of(this.getDefaultRole(model.page));
         })
      );
   }

   GetRole(roleName: string): Observable<RoleModel | undefined> {
      return this.RoleHttpService.GetRole(roleName).pipe(
         map((role: RoleModel) => role),
         catchError((err) => {
            console.error('Get Role error:', err);
            return of(undefined);
         })
      );
   }

   GetRolesNotInUser(roleName: string): Observable<RoleModel[]> {
      return this.RoleHttpService.GetRolesNotInUser(roleName).pipe(
         map((roles: RoleModel[]) => {
            if (roles) {
               return roles;
            }

            return [];
         }),
         catchError((err) => {
            console.error('GetRolesNotInUser error:', err);
            return of([]);
         })
      );
   }

   GetPremissions(): Observable<PremissionModel[]> {
      return this.RoleHttpService.GetPremissions().pipe(
         map((roles: PremissionModel[]) => {
            if (roles) {
               return roles;
            }

            return [];
         }),
         catchError((err) => {
            console.error('Role Search error:', err);
            return of([]);
         }));
   }

   CreateRole(model: CreateRoleModel): Observable<AppResult<RoleModel> | undefined> {
      this.isLoadingSubject.next(true);
      return this.RoleHttpService.AddRole(model).pipe(
         map((result: AppResult<RoleModel>) => result),
         catchError((err) => {
            console.error('Create role error:', err);
            return of(err.error);
         }),
         finalize(() => this.isLoadingSubject.next(false))
      );
   }

   AssignRole(model: AssignRoleModel): Observable<AppResult<string> | undefined> {
      this.isLoadingSubject.next(true);
      return this.RoleHttpService.AssignRole(model).pipe(
         map((result: AppResult<string>) => result),
         catchError((err) => {
            console.error('Create role error:', err);
            return of(err.error);
         }),
         finalize(() => this.isLoadingSubject.next(false))
      );
   }

   UpdateRole(model: UpdateRoleModel): Observable<AppResult<RoleModel> | undefined> {
      this.isLoadingSubject.next(true);
      return this.RoleHttpService.UpdateRole(model).pipe(
         map((result: AppResult<RoleModel>) => result),
         catchError((err) => {
            console.error('Update role error:', err);
            return of(err.error);
         }),
         finalize(() => this.isLoadingSubject.next(false))
      );
   }

   DeleteRole(roleName: string): Observable<AppResult<string> | undefined> {
      return this.RoleHttpService.DeleteRole(roleName).pipe(
         map((result: AppResult<string>) => result),
         catchError((err) => {
            console.error('Delete role error:', err);
            return of(err.error);
         })
      );
   }

   private getDefaultRole(page: number): PaginatedList<RoleModel> {
      return {
         items: [],
         pageNumber: page,
         totalCount: 0,
         totalPages: 0
      }
   }

   ngOnDestroy() {
      this.unsubscribe.forEach((sb) => sb.unsubscribe());
   }
}
